/**
 * Shipping cost for this offer, same rule as the product page:
 * the cheapest matrix rate of the product's shipping group that the price reaches.
 */
export function offerShippingRate (product, offerPrice, rates, attributesByCode) {
  const price = Number(offerPrice)
  if (!Number.isFinite(price)) return null
  const options = attributesByCode && attributesByCode.package_id && attributesByCode.package_id.options
  let group = ''
  if (options && product && product.package_id != null && product.package_id !== '') {
    const match = Object.values(options).find(option => parseInt(option.value, 10) === parseInt(product.package_id, 10))
    group = match && match.label ? String(match.label) : ''
  }
  const applicable = (rates || []).filter(rate => {
    const rateGroup = rate && rate.shipping_group ? String(rate.shipping_group) : ''
    return group ? rateGroup === group : !rateGroup
  }).filter(rate => price >= parseFloat(rate.condition_from_value))
    .sort((a, b) => parseFloat(a.price) - parseFloat(b.price))
  if (!applicable.length) return null
  const value = parseFloat(applicable[0].price)
  return Number.isFinite(value) ? Math.round(value * 100) / 100 : null
}

function daysFromText (text) {
  const normalized = String(text).toLowerCase().replace(/\./g, ' ').replace(/\s+/g, ' ').trim()
  const range = normalized.match(/(\d+)\s*[-–]\s*(\d+)/)
  const single = normalized.match(/(\d+)/)
  if (!range && !single) return null
  let min = range ? parseInt(range[1], 10) : parseInt(single[1], 10)
  let max = range ? parseInt(range[2], 10) : min
  if (!(min > 0) || !(max >= min)) return null
  if (/woche/.test(normalized)) {
    min *= 7
    max *= 7
  } else if (!/tag|werktag/.test(normalized)) {
    return null
  }
  return { min, max }
}

/** Split a total window so handling + transit still equals the stated delivery time. */
function splitWindow (min, max) {
  if (max <= 7) {
    return { handling: [0, 1], transit: [min, max] }
  }
  const transitMin = 1
  const transitMax = Math.min(5, Math.max(1, max - min || 1))
  const handlingMin = min - transitMin
  const handlingMax = max - transitMax
  if (handlingMin >= 0 && handlingMax >= handlingMin) {
    return { handling: [handlingMin, handlingMax], transit: [transitMin, transitMax] }
  }
  return { handling: [Math.max(0, min - 1), Math.max(1, max - 1)], transit: [1, 1] }
}

function quantitativeDays (min, max) {
  return {
    '@type': 'QuantitativeValue',
    minValue: min,
    maxValue: max,
    unitCode: 'DAY'
  }
}

/**
 * OfferShippingDetails for the product badge and the matrix rate that applies at this price.
 */
export function offerShippingDetails ({ deliveryTime, country, rate, currency }) {
  const text = typeof deliveryTime === 'string' ? deliveryTime.trim() : ''
  const hasRate = rate != null && Number.isFinite(Number(rate))
  const destination = typeof country === 'string' ? country.trim() : ''
  if (!text && !hasRate && !destination) return null
  const details = { '@type': 'OfferShippingDetails' }
  if (hasRate) {
    details.shippingRate = {
      '@type': 'MonetaryAmount',
      value: Math.round(Number(rate) * 100) / 100,
      currency: currency || 'EUR'
    }
  }
  if (destination) {
    details.shippingDestination = {
      '@type': 'DefinedRegion',
      addressCountry: destination
    }
  }
  const windowDays = text ? daysFromText(text) : null
  if (text || windowDays) {
    details.deliveryTime = { '@type': 'ShippingDeliveryTime' }
    if (text) details.deliveryTime.description = text
    if (windowDays) {
      const split = splitWindow(windowDays.min, windowDays.max)
      details.deliveryTime.handlingTime = quantitativeDays(split.handling[0], split.handling[1])
      details.deliveryTime.transitTime = quantitativeDays(split.transit[0], split.transit[1])
    }
  }
  return details
}
